import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { IonCard, IonCardContent } from "@ionic/react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import RichTextArea from "@components/fields/RichTextArea";
import Form from "@components/forms/Form";
import styles from "@components/forms/InternalNoteForm.module.css";
import useCreateInternalNote from "@hooks/mutations/useCreateInternalNote";
import useFormPersist from "@hooks/useFormPersist";
import {
  ConsultFragment as ConsultType,
  InternalNoteKindEnum,
  InternalNoteFragment as InternalNoteType,
  InternalNoteUpdateDocument,
  InternalNoteUpdateMutation,
  InternalNoteUpdateMutationVariables
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";

type FormData = {
  body: string;
  title?: string | null;
};

type Props = {
  consult?: ConsultType;
  internalNote?: InternalNoteType | null;
  journeyId: string;
  kind?: InternalNoteKindEnum;
  onCancel?: () => void;
  onEdit?: () => void;
  onSuccess?: () => void;
  skipCancel?: boolean;
};

const InternalNoteForm = ({
  consult,
  internalNote,
  journeyId,
  kind = InternalNoteKindEnum.NOTE,
  onCancel,
  onSuccess,
  skipCancel = false
}: Props) => {
  const defaultValues: FormData = {
    body: internalNote?.body ?? "",
    title: internalNote?.title ?? null
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [editorRefresh, setEditorRefresh] = useState(false);

  const [updateInternalNote] = useMutation<InternalNoteUpdateMutation, InternalNoteUpdateMutationVariables>(
    InternalNoteUpdateDocument
  );
  const createInternalNote = useCreateInternalNote({ consultId: consult?.id, journeyId });

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      body: yup.string().required(intl.formatMessage({ id: "errors.internalNote.body.blank" })),
      title: yup.string().nullable()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const formPersist = useFormPersist("internalNoteForm", {
    form,
    onDataRestored: () => {
      setEditorRefresh(!editorRefresh);
    },
    // The undefined in here is to make this continue working after the initial consult report id
    // was removed.
    uniqueId: `${journeyId}-${undefined}-${internalNote?.id}-${consult?.id}`
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    if (internalNote?.id && kind === InternalNoteKindEnum.NOTE) {
      return updateInternalNote({
        variables: {
          ...form.getValues(),
          id: internalNote.id
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.internalNoteUpdate,
          intl,
          onSuccess: () => {
            formPersist.clear();
            if (onSuccess) {
              onSuccess();
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: "errors.internalNote"
        });
      });
    }
    return createInternalNote({
      ...form.getValues(),
      consultReportId: consult?.consultReport?.id,
      journeyId,
      kind
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response?.internalNoteCreate,
        intl,
        onSuccess: () => {
          if (onSuccess) {
            onSuccess();
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.internalNote"
      });
      form.reset();
    });
  }, [
    internalNote?.id,
    kind,
    createInternalNote,
    form,
    consult?.consultReport?.id,
    journeyId,
    updateInternalNote,
    intl,
    onSuccess,
    formPersist
  ]);

  return (
    <IonCard className={styles.card}>
      <IonCardContent>
        <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
          <BaseErrors errors={baseErrors} />
          <div className="ion-margin-vertical">
            {kind === InternalNoteKindEnum.NOTE && (
              <Input<FormData>
                error={form.formState.errors.title}
                placeholder={intl.formatMessage({ id: "pages.carePlan.base.expert.title" })}
                register={form.register("title")}
              />
            )}
            <RichTextArea
              contentRefreshSwitch={editorRefresh}
              control={form.control}
              error={form.formState.errors.body}
              extraStyles={kind === InternalNoteKindEnum.NOTE ? styles.editor : ""}
              journeyId={journeyId}
              name="body"
              sharableURLSHidden={kind !== InternalNoteKindEnum.NOTE}
              size="small"
            />
          </div>

          {kind !== InternalNoteKindEnum.NOTE ? (
            <div>
              <button
                className={styles.button + " " + styles.save}
                data-test="submit-button"
                onClick={() => {
                  form.reset();
                  onCancel?.();
                }}
              >
                <FormattedMessage id="dictionary.cancel" />
              </button>
              <button
                className={styles.button + " " + styles.cancel}
                data-test="submit-button"
                disabled={!form.formState.isValid || form.formState.isSubmitting}
                type="submit"
              >
                <FormattedMessage id="dictionary.save" />
              </button>
            </div>
          ) : (
            <div className={!internalNote ? styles.buttonsInForm : ""}>
              <FormButtons
                hookForm={form}
                narrowMargin
                onCancel={onCancel}
                saveKey={internalNote ? "dictionary.updateNote" : "dictionary.addNote"}
                skipCancel={skipCancel}
              />
            </div>
          )}
        </Form>
      </IonCardContent>
    </IonCard>
  );
};

export default InternalNoteForm;
